import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useProjectDataForm } from '../hooks/useProjectDataForm';
import ProjectLocationMap from './ProjectLocationMap';
import CustomerSelect from '../../../tools/shared/CustomerSelect/CustomerSelect';
import '../../Projects.css';
import '../ProjectModal.css';

const STATUS_OPTIONS = [
    { value: 'active', label: 'Aktiv', color: '#10b981' },
    { value: 'paused', label: 'Inaktiv', color: '#f59e0b' },
    { value: 'completed', label: 'Abgeschlossen', color: '#0284c7' },
    { value: 'archived', label: 'Archiv', color: '#9ca3af' }
];

/**
 * @file ProjectDataForm.jsx
 * @description Formular für die Stammdaten eines Projekts inklusive Karte.
 */
const ProjectDataForm = forwardRef(({ mode, initialData, onSubmit, onDelete, projects = [], deletedProjects = [], onClose, hideFooter = false }, ref) => {
    const {
        form,
        setForm,
        currentMode,
        setCurrentMode,
        searchQuery,
        attemptedSubmit,
        setAttemptedSubmit,
        projectNumberError,
        setProjectNumberError,
        nameError,
        setNameError,
        validateAndSubmit,
        handleLocationSelect
    } = useProjectDataForm({ initialData, mode, projects, deletedProjects, onSubmit });

    const addressRef = useRef(null);
    const descriptionRef = useRef(null);

    useEffect(() => {
        if (addressRef.current) {
            addressRef.current.style.height = 'auto';
            addressRef.current.style.height = addressRef.current.scrollHeight + 'px';
        }
    }, [form.address]);

    useEffect(() => {
        if (descriptionRef.current) {
            descriptionRef.current.style.height = 'auto';
            descriptionRef.current.style.height = descriptionRef.current.scrollHeight + 'px';
        }
    }, [form.description]);

    useImperativeHandle(ref, () => ({
        submitForm: () => validateAndSubmit(),
        getFormState: () => form
    }));

    return (
        <form onSubmit={(e) => { e.preventDefault(); validateAndSubmit(); }} style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px', padding: '20px' }}>
            <div className="form-left">
                <div className="form-group">
                    <label>Projektnummer*</label>
                    <input
                        type="text"
                        className={(attemptedSubmit && !form.project_number?.trim()) || projectNumberError ? 'field-error' : ''}
                        value={form.project_number}
                        onChange={e => {
                            const value = e.target.value;
                            setForm({ ...form, project_number: value });
                            setAttemptedSubmit(false);
                            if (!value.trim()) {
                                setProjectNumberError('');
                            }
                        }}
                        placeholder="Z.B. 2024-001"
                        disabled={currentMode === 'view'}
                        aria-invalid={Boolean(projectNumberError)}
                    />
                    {(attemptedSubmit && !form.project_number?.trim()) && (
                        <span className="error-text">Fülle dieses Feld aus.</span>
                    )}
                    {projectNumberError && (
                        <span className="error-text">{projectNumberError}</span>
                    )}
                </div>

                <div className="form-group">
                    <label>Projektname*</label>
                    <input
                        type="text"
                        className={(attemptedSubmit && !form.name?.trim()) || nameError ? 'field-error' : ''}
                        value={form.name}
                        onChange={e => {
                            const value = e.target.value;
                            setForm({ ...form, name: value });
                            setAttemptedSubmit(false);
                            if (!value.trim()) {
                                setNameError('');
                            }
                        }}
                        placeholder="Z.B. Neubau Kita"
                        disabled={currentMode === 'view'}
                        aria-invalid={Boolean(nameError)}
                    />
                    {(attemptedSubmit && !form.name?.trim()) && (
                        <span className="error-text">Fülle dieses Feld aus.</span>
                    )}
                    {nameError && (
                        <span className="error-text">{nameError}</span>
                    )}
                </div>

                <div className="form-group" style={{ zIndex: 100 }}>
                    <label>Kunde (optional)</label>
                    <CustomerSelect 
                        value={form.customer_id}
                        onChange={(val) => setForm({ ...form, customer_id: val })}
                        disabled={currentMode === 'view'}
                    />
                </div>

                <div className="form-group">
                    <label>Adresse / Standort</label>
                    <textarea
                        ref={addressRef}
                        style={{ fontFamily: 'inherit', resize: 'none', overflow: 'hidden', minHeight: '40px' }}
                        value={form.address}
                        onChange={e => setForm({ ...form, address: e.target.value })}
                        placeholder="Wird automatisch befüllt oder manuell eingeben..."
                        rows={1}
                        disabled={currentMode === 'view'}
                    />
                </div>

                <div className="form-group">
                    <label>Beschreibung</label>
                    <textarea
                        ref={descriptionRef}
                        style={{ fontFamily: 'inherit', resize: 'none', overflow: 'hidden', minHeight: '40px' }}
                        value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        placeholder="Optionale Beschreibung des Bauvorhabens..."
                        rows={1}
                        disabled={currentMode === 'view'}
                    />
                </div>

                <div className="form-group">
                    <label>Status</label>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {STATUS_OPTIONS.map(option => (
                            <button
                                key={option.value}
                                type="button"
                                onClick={() => setForm({ ...form, status: option.value })}
                                disabled={currentMode === 'view'}
                                style={{
                                    padding: '8px 12px', borderRadius: '6px', border: '1px solid',
                                    borderColor: form.status === option.value ? option.color : '#e5e7eb',
                                    backgroundColor: form.status === option.value ? option.color : '#fff',
                                    color: form.status === option.value ? '#fff' : '#374151',
                                    cursor: currentMode === 'view' ? 'default' : 'pointer',
                                    fontSize: '0.85rem', fontWeight: '500', transition: 'all 0.2s',
                                    opacity: currentMode === 'view' && form.status !== option.value ? 0.5 : 1
                                }}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '5px', textAlign: 'right' }}>* Pflichtfeld</div>

                {!hideFooter && (
                    <div className="modal-actions" style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                        {currentMode === 'view' ? (
                            <>
                                <button type="button" onClick={onClose} className="btn-cancel" style={{ flex: 1 }}>Schließen</button>
                                {initialData?.can_edit_project && (
                                    <button type="button" onClick={() => setCurrentMode('edit')} className="btn-primary" style={{ flex: 1 }}>Bearbeiten</button>
                                )}
                            </>
                        ) : (
                            <>
                                <button type="button" onClick={onClose} className="btn-cancel" style={{ flex: 1 }}>Abbrechen</button>
                                {currentMode === 'edit' && initialData?.can_delete_project && (
                                    <button type="button" onClick={() => onDelete(form)} className="btn-danger" style={{ flex: 1 }}>Löschen</button>
                                )}
                                <button type="submit" className="btn-save" style={{ flex: 1 }}>Speichern</button>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className="form-right">
                <ProjectLocationMap
                    latitude={form.latitude}
                    longitude={form.longitude}
                    searchQuery={searchQuery}
                    disabled={currentMode === 'view'}
                    onLocationSelect={handleLocationSelect}
                />
            </div>
        </form>
    );
});

export default ProjectDataForm;
